import { Feature, Position } from 'geojson';
import { useInjection } from 'inversify-react';
import { LatLngBounds } from 'leaflet';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { pointCoordsToLatLng } from '../../../helpers/geometry/polygon.helpers';
import { IAnnotationType } from '../../projectDetails/sub/tools/projectDetailsTools.models';
import { IUserStore } from '../../user/user.store';
import { IAnnotationService } from '../annotation.service';
import { ISegmentation } from '../annotations.interface';
import { IAnnotationsStore } from '../annotations.store';
import { IAnnotationUiStore } from '../annotationUi.store';
import { DrawingComponent } from '../components/DrawingComponent';
import { IFreeDrawSegmentationService } from '../freeDrawSegmentation.service';
import { IAnnotationTypeBl } from '../submodules/annotationTypes/annotationType.bl';
import { IAnnotationTypeStore } from '../submodules/annotationTypes/annotationType.store';
import { IUndoRedoHistory } from '../undoRedoHistory.service';

interface IProps {
  disableCursorGuides: boolean;
}

export enum DRAW_CONTROL_MODE {
  CREATE = 'CREATE',
  EDIT = 'EDIT',
}

export const DrawingContainer = observer((props: IProps): ReactElement => {
  const { projectId } = useParams<{ projectId: string }>();

  const annotationService = useInjection(IAnnotationService.$);
  const annotationTypeBl = useInjection(IAnnotationTypeBl.$);
  const annotationTypeStore = useInjection(IAnnotationTypeStore.$);
  const freeDrawSegmentationService = useInjection(IFreeDrawSegmentationService.$);
  const uiStore = useInjection(IAnnotationUiStore.$);
  const undoRedoHistory = useInjection(IUndoRedoHistory.$);
  const userStore = useInjection(IUserStore.$);
  const annotationsStore = useInjection(IAnnotationsStore.$);

  const handleAnnotationCreated = (feature: Feature): void => {
    if (freeDrawSegmentationService.freeDrawFeature) {
      freeDrawSegmentationService.updateFreeDrawFeature(feature);
      return;
    }
    if (checkImageIntersection(feature) && annotationTypeStore.activeAnnotationTypeId) {
      const newSegmentation = annotationService.createNewSegmentation(annotationTypeStore.activeAnnotationTypeId, feature);
      annotationService.addNewSegmentation(newSegmentation);
      showNewSegmentation(annotationTypeStore.activeAnnotationTypeId, newSegmentation);
      undoRedoHistory.clearDrawingHistory();
      undoRedoHistory.addCommand(undoRedoHistory.getNewChangeStateCommand());
    }
  };

  const showNewSegmentation = (activeAnnotationTypeId: string, newSegmentation: ISegmentation): void => {
    const activeMarkingToolOptions = annotationTypeStore.annotationTypesOptions.find((o) => o.id === activeAnnotationTypeId);

    if (activeMarkingToolOptions !== undefined && !activeMarkingToolOptions.isVisible) {
      annotationTypeBl.setAnnotationTypeOptions(projectId, { ...activeMarkingToolOptions, isVisible: true });

      const segmentationToHide = annotationsStore.segmentations
        .filter((s) => activeAnnotationTypeId === s.feature.properties?.annotationTypeId && s.feature.id !== newSegmentation.feature.id)
        .map((s) => ({ segmentationId: s.id, featureId: s.feature.id }));
      segmentationToHide.forEach((s) => {
        annotationService.hideSegmentation(s.featureId, s.segmentationId);
      });
    }
  };

  const handleAnnotationUpdated = async (id: string | undefined, feature: Feature): Promise<void> => {
    if (!id) {
      return;
    }
    if (checkImageIntersection(feature)) {
      annotationService.updateSegmentation(id, feature);
    } else {
      await handleAnnotationRemove(id);
    }
  };

  const checkImageIntersection = (feature: Feature): boolean => {
    if (feature.geometry.type === 'GeometryCollection') {
      return false;
    }
    const coordinates = feature.geometry.coordinates;

    const imageBB = new LatLngBounds([0, 0], [annotationsStore.image?.height ?? 0, annotationsStore.image?.width ?? 0]);

    let positions: Position[] = [];
    if (Array.isArray(coordinates[0]) && Array.isArray(coordinates[0][0])) {
      positions = ([] as Position[]).concat(...(coordinates as Position[][]));
    } else if (Array.isArray(coordinates[0])) {
      positions = coordinates as Position[];
    } else {
      positions = [coordinates as Position];
    }

    const latlngs = positions.map((x) => pointCoordsToLatLng(x));
    return latlngs.some((x) => imageBB.contains(x));
  };

  const handleFreeDrawStarted = (annotation: Feature): void => {
    if (!freeDrawSegmentationService.freeDrawFeature) {
      const activeAnnotationTypeId = annotationTypeStore.activeAnnotationTypeId;
      const newAnnotation = annotationService.createNewSegmentation(activeAnnotationTypeId as string, annotation);
      freeDrawSegmentationService.setFreeDrawFeature(newAnnotation.feature);
      annotationService.deselectSegmentation();
      annotationService.setQuestionsAsCurrent(newAnnotation.questions);
    }

    freeDrawSegmentationService.startFreeDraw();
  };

  const handleFreeDrawCancel = (): Promise<boolean> | undefined => {
    return freeDrawSegmentationService.clearAsync();
  };

  const handleFreeDrawDelete = (): void => {
    freeDrawSegmentationService.clear(true);
  };

  const handleAnnotationRemove = async (id: string): Promise<void> => {
    freeDrawSegmentationService.clear(true);
    annotationService.deleteSegmentation(id);
    await annotationTypeBl.handleAnnotationRemoveInDrawingContainer();
  };

  const activeAnnotationTypeId = annotationTypeStore.activeAnnotationTypeId;
  const activeAnnotationType = annotationService.getAnnotationType(activeAnnotationTypeId as string);
  const getControlMode = (activeAnnotationType: IAnnotationType | undefined): DRAW_CONTROL_MODE =>
    activeAnnotationType !== undefined || annotationsStore.drawFocusFrameForId ? DRAW_CONTROL_MODE.CREATE : DRAW_CONTROL_MODE.EDIT;

  const areCursorGuidesEnabled = !props.disableCursorGuides && userStore.areCursorGuidesEnabled;

  return (
    <DrawingComponent
      activeAnnotationTypeId={activeAnnotationTypeId}
      activeAnnotationTypeOptions={annotationTypeStore.annotationTypesOptions.find((o) => o.id === activeAnnotationTypeId)}
      color={activeAnnotationType ? activeAnnotationType.color : 'black'}
      fillOpacity={userStore.opacityLevel / 100}
      controlMode={getControlMode(activeAnnotationType)}
      stopAction={!annotationsStore.image}
      selectorType={activeAnnotationType ? activeAnnotationType.selectorType : null}
      selectedSegmentation={annotationsStore.selectedSegmentation}
      freeDrawInProgress={freeDrawSegmentationService.freeDrawInProgress}
      freeDrawSegmentationFeature={toJS(freeDrawSegmentationService.freeDrawFeature)}
      currentCursorPosition={uiStore.cursorLatLng}
      onAnnotationCreated={handleAnnotationCreated}
      onAnnotationCopied={(id) => {
        annotationService.copySegmentation(id);
      }}
      onAnnotationPasted={(annotation, id) => {
        annotationService.pasteSegmentation(annotation, id);
      }}
      onAnnotationEdited={handleAnnotationUpdated}
      onAnnotationRemove={handleAnnotationRemove}
      onMouseMove={(latng) => {
        uiStore.setCurrentCursorPosition(latng);
      }}
      undoRedoHistory={undoRedoHistory}
      freeDrawSegmentationService={freeDrawSegmentationService}
      onEditDisabled={() => {
        annotationService.deselectSegmentation();
      }}
      onEditEnabled={() => {
        annotationService.enableEdit();
      }}
      onFreeDrawStarted={handleFreeDrawStarted}
      onFreeDrawCancel={handleFreeDrawCancel}
      onFreeDrawDelete={handleFreeDrawDelete}
      annotationService={annotationService}
      isImprovedVisibilityCursorEnabled={userStore.isImprovedVisibilityCursorEnabled}
      areCursorGuidesEnabled={areCursorGuidesEnabled}
    />
  );
});
